import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion as Motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Utils
import { scrollToTop } from "@/lib/utilsUI";

export default function DropdownMenu({
  trigger,
  options = [],
  direction = "down",
  align = "start",
  className = "",
  menuClassName = "",
  classContent = "bg-white",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen]);

  // Get position classes based on direction and alignment
  const getPositionClasses = () => {
    const positions = {
      down: {
        start: "top-full left-0 mt-2",
        center: "top-full left-1/2 -translate-x-1/2 mt-2",
        end: "top-full right-0 mt-2",
      },
      up: {
        start: "bottom-full left-0 mb-2",
        center: "bottom-full left-1/2 -translate-x-1/2 mb-2",
        end: "bottom-full right-0 mb-2",
      },
      left: {
        start: "right-full top-0 mr-2",
        center: "right-full top-1/2 -translate-y-1/2 mr-2",
        end: "right-full bottom-0 mr-2",
      },
      right: {
        start: "left-full top-0 ml-2",
        center: "left-full top-1/2 -translate-y-1/2 ml-2",
        end: "left-full bottom-0 ml-2",
      },
    };

    return positions[direction]?.[align] || positions.down.start;
  };

  // Get animation variants based on direction
  const getAnimationVariants = () => {
    const variants = {
      down: {
        initial: { opacity: 0, y: -10, scale: 0.95 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: -10, scale: 0.95 },
      },
      up: {
        initial: { opacity: 0, y: 10, scale: 0.95 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 10, scale: 0.95 },
      },
      left: {
        initial: { opacity: 0, x: 10, scale: 0.95 },
        animate: { opacity: 1, x: 0, scale: 1 },
        exit: { opacity: 0, x: 10, scale: 0.95 },
      },
      right: {
        initial: { opacity: 0, x: -10, scale: 0.95 },
        animate: { opacity: 1, x: 0, scale: 1 },
        exit: { opacity: 0, x: -10, scale: 0.95 },
      },
    };

    return variants[direction] || variants.down;
  };

  const handleItemClick = (option) => {
    if (option.onClick) {
      option.onClick();
    }
    setIsOpen(false);
  };

  const renderMenuItem = (option, index) => {
    if (option.divider) {
      return (
        <div
          key={`divider-${index}`}
          className="my-1 border-t border-aw-bluegray"
        />
      );
    }

    const isLink = option.type === "link";
    const isDisabled = option.disabled;

    const commonClasses = `
      flex items-center w-full px-2 py-1.5 text-left text-sm rounded-md
      ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
      transition-colors duration-150
      ${option.className || "text-aw-bluedark"}
    `;

    const content = (
      <>
        {option.icon && (
          <FontAwesomeIcon icon={option.icon} className="mr-3 w-4 h-4" />
        )}
        <span>{option.label}</span>
      </>
    );

    if (isLink && !isDisabled) {
      return (
        <Link
          key={index}
          to={option.to}
          className={commonClasses}
          onClick={() => {
            setIsOpen(false);
            scrollToTop();
          }}
        >
          {content}
        </Link>
      );
    }
    if (option.type !== "none") {
      return (
        <button
          key={index}
          className={commonClasses}
          onClick={() => !isDisabled && handleItemClick(option)}
          disabled={isDisabled}
        >
          {content}
        </button>
      );
    } else {
      return <></>;
    }
  };

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Trigger */}
      <div
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="cursor-pointer"
      >
        {trigger}
      </div>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <Motion.div
            ref={dropdownRef}
            initial={getAnimationVariants().initial}
            animate={getAnimationVariants().animate}
            exit={getAnimationVariants().exit}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 30,
              duration: 0.2,
            }}
            className={`
              absolute z-50 min-w-[12rem] 
              ${classContent} rounded-lg shadow-lg border border-aw-bluegray
              overflow-hidden
              ${getPositionClasses()}
              ${menuClassName}
            `}
          >
            <div className="">
              {options.map((option, index) => (
                <Motion.div
                  key={index}
                  initial={{
                    opacity: 0,
                    x:
                      direction === "left"
                        ? 10
                        : direction === "right"
                        ? -10
                        : 0,
                  }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: index * 0.05,
                    duration: 0.15,
                  }}
                >
                  {renderMenuItem(option, index)}
                </Motion.div>
              ))}
            </div>
          </Motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
